import { useEffect, useMemo, useState } from 'react'
import {
  AlertTriangle,
  BriefcaseBusiness,
  ChevronRight,
  Plus,
  ReceiptText,
  Search,
  UserPlus,
  X,
} from 'lucide-react'
import type { Client, Invoice, InvoiceStatus, WorkProject } from '../types'
import {
  clientMatchesCompanyQuery,
  displayCompanyName,
  sortClientsByDisplayName,
} from '../lib/companyName'
import { invoiceTiming, sortInvoicesByUrgency, todayInMoscow } from '../workspace/billing'

interface OperationsHubProps {
  mode?: 'overview' | 'projects' | 'invoices'
  clients: Client[]
  projects: WorkProject[]
  invoices: Invoice[]
  onSaveProject: (project: WorkProject) => void
  onDeleteProject: (project: WorkProject) => boolean
  onSaveInvoice: (invoice: Invoice) => void
  onDeleteInvoice: (invoice: Invoice) => boolean
  onCreateClient: (input: { company: string; taxId: string; paymentTermsDays: number }) => Client
}

type Registry = 'projects' | 'invoices' | null
type NewClientDraft = { company: string; taxId: string; paymentTermsDays: number }

const STANDARD_PAYMENT_TERMS = [0, 30, 45, 60, 90]

function money(kopecks: number): string {
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 2,
  }).format(kopecks / 100)
}

function projectDraft(clients: Client[]): WorkProject {
  const now = new Date().toISOString()
  const client = clients[0]
  return {
    id: crypto.randomUUID(),
    number: '',
    name: '',
    clientId: client?.id ?? '',
    clientNameSnapshot: client?.company ?? '',
    status: 'active',
    createdAt: now,
    updatedAt: now,
  }
}

function invoiceDraft(): Invoice {
  const now = new Date().toISOString()
  return {
    id: crypto.randomUUID(),
    number: '',
    clientId: '',
    clientNameSnapshot: '',
    projectId: null,
    projectNameSnapshot: '',
    amountKopecks: 0,
    currency: 'RUB',
    issuedOn: todayInMoscow(),
    status: 'issued',
    shippedOn: null,
    paymentTermsDays: 30,
    paidOn: null,
    createdAt: now,
    updatedAt: now,
  }
}

export function OperationsHub({
  mode = 'overview',
  clients,
  projects,
  invoices,
  onSaveProject,
  onDeleteProject,
  onSaveInvoice,
  onDeleteInvoice,
  onCreateClient,
}: OperationsHubProps) {
  const [registry, setRegistry] = useState<Registry>(mode === 'overview' ? null : mode)
  const [projectEditor, setProjectEditor] = useState<WorkProject | null>(null)
  const [invoiceEditor, setInvoiceEditor] = useState<Invoice | null>(null)
  const [query, setQuery] = useState('')
  const [formError, setFormError] = useState('')
  const [clientQuery, setClientQuery] = useState('')
  const [clientPickerOpen, setClientPickerOpen] = useState(false)
  const [newClientEditor, setNewClientEditor] = useState<NewClientDraft | null>(null)

  useEffect(() => {
    if (mode !== 'overview') setRegistry(mode)
  }, [mode])

  useEffect(() => {
    if (!registry && !projectEditor && !invoiceEditor && !newClientEditor) return
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      if (newClientEditor) setNewClientEditor(null)
      else if (clientPickerOpen) setClientPickerOpen(false)
      else if (invoiceEditor) setInvoiceEditor(null)
      else if (projectEditor) setProjectEditor(null)
      else if (mode === 'overview') setRegistry(null)
    }
    window.addEventListener('keydown', closeOnEscape)
    return () => window.removeEventListener('keydown', closeOnEscape)
  }, [clientPickerOpen, invoiceEditor, mode, newClientEditor, projectEditor, registry])

  const activeProjects = projects.filter((project) => project.status === 'active')
  const urgentInvoices = useMemo(
    () => sortInvoicesByUrgency(invoices.filter((invoice) => !['paid', 'cancelled'].includes(invoice.status))),
    [invoices],
  )
  const overdue = urgentInvoices.filter((invoice) => invoiceTiming(invoice).status === 'overdue').length
  const dueSoon = urgentInvoices.filter((invoice) => {
    const status = invoiceTiming(invoice).status
    return status === 'due_soon' || status === 'due_today'
  }).length

  const visibleProjects = activeProjects.slice(0, 4)
  const visibleInvoices = urgentInvoices.slice(0, 4)
  const sortedClients = useMemo(() => sortClientsByDisplayName(clients), [clients])
  const matchingClients = useMemo(
    () => sortedClients.filter((client) => clientMatchesCompanyQuery(client, clientQuery)).slice(0, 12),
    [clientQuery, sortedClients],
  )
  const normalizedQuery = query.trim().toLocaleLowerCase('ru')
  const registryProjects = projects.filter((project) =>
    `${project.number} ${project.name} ${project.clientNameSnapshot}`
      .toLocaleLowerCase('ru')
      .includes(normalizedQuery),
  )
  const registryInvoices = sortInvoicesByUrgency(invoices).filter((invoice) =>
    `${invoice.number} ${invoice.clientNameSnapshot} ${invoice.projectNameSnapshot}`
      .toLocaleLowerCase('ru')
      .includes(normalizedQuery),
  )

  function chooseProjectClient(clientId: string) {
    const client = clients.find((item) => item.id === clientId)
    setProjectEditor((current) => current ? {
      ...current,
      clientId,
      clientNameSnapshot: client?.company ?? '',
    } : current)
  }

  function chooseInvoiceClient(clientId: string) {
    const client = clients.find((item) => item.id === clientId)
    const currentProject = projects.find((item) => item.id === invoiceEditor?.projectId)
    setInvoiceEditor((current) => current ? {
      ...current,
      clientId,
      clientNameSnapshot: client?.company ?? '',
      paymentTermsDays: client?.paymentTermsDays ?? 30,
      projectId: currentProject?.clientId === clientId ? current.projectId : null,
      projectNameSnapshot: currentProject?.clientId === clientId ? current.projectNameSnapshot : '',
    } : current)
    setClientQuery(client ? displayCompanyName(client.company) : '')
    setClientPickerOpen(false)
  }

  function openInvoiceEditor(invoice: Invoice) {
    setFormError('')
    setClientQuery(invoice.clientNameSnapshot ? displayCompanyName(invoice.clientNameSnapshot) : '')
    setClientPickerOpen(false)
    setInvoiceEditor(invoice)
  }

  function submitNewClient() {
    if (!newClientEditor?.company.trim()) return
    const client = onCreateClient({
      company: newClientEditor.company.trim(),
      taxId: newClientEditor.taxId.trim(),
      paymentTermsDays: newClientEditor.paymentTermsDays,
    })
    setInvoiceEditor((current) => current ? {
      ...current,
      clientId: client.id,
      clientNameSnapshot: client.company,
      paymentTermsDays: client.paymentTermsDays ?? 30,
      projectId: null,
      projectNameSnapshot: '',
    } : current)
    setClientQuery(displayCompanyName(client.company))
    setClientPickerOpen(false)
    setNewClientEditor(null)
  }

  function changeInvoiceStatus(status: InvoiceStatus) {
    setInvoiceEditor((current) => {
      if (!current) return current
      return {
        ...current,
        status,
        shippedOn: status === 'shipped' || status === 'paid'
          ? current.shippedOn ?? todayInMoscow()
          : null,
        paidOn: status === 'paid' ? current.paidOn ?? todayInMoscow() : null,
      }
    })
  }

  function submitProject() {
    if (!projectEditor?.number.trim() || !projectEditor.name.trim() || !projectEditor.clientId) return
    onSaveProject({ ...projectEditor, number: projectEditor.number.trim(), name: projectEditor.name.trim(), updatedAt: new Date().toISOString() })
    setProjectEditor(null)
  }

  function submitInvoice() {
    if (!invoiceEditor?.number.trim() || invoiceEditor.amountKopecks < 0) return
    if (!invoiceEditor.clientId) {
      setFormError('Выберите клиента из найденных вариантов или создайте нового.')
      setClientPickerOpen(true)
      return
    }
    if (invoiceEditor.shippedOn && invoiceEditor.shippedOn < invoiceEditor.issuedOn) {
      setFormError('Дата отгрузки не может быть раньше даты счёта.')
      return
    }
    if (invoiceEditor.paidOn && invoiceEditor.shippedOn && invoiceEditor.paidOn < invoiceEditor.shippedOn) {
      setFormError('Дата оплаты не может быть раньше даты отгрузки.')
      return
    }
    setFormError('')
    onSaveInvoice({ ...invoiceEditor, number: invoiceEditor.number.trim(), updatedAt: new Date().toISOString() })
    setInvoiceEditor(null)
  }

  return (
    <section className={`operations-hub operations-mode-${mode}`}>
      <div className="operations-heading">
        <div>
          <span className="eyebrow">Проекты и расчёты</span>
          <h2>Текущая работа</h2>
        </div>
        <span>Личный контроль. Официальные данные сверяйте с CRM и 1С.</span>
      </div>

      <div className="operations-columns">
        <article className="operations-card">
          <header>
            <div>
              <BriefcaseBusiness size={18} />
              <span><strong>{activeProjects.length}</strong> активных проектов</span>
            </div>
            <button type="button" onClick={() => { setFormError(''); setProjectEditor(projectDraft(clients)) }}>
              <Plus size={16} /> Проект
            </button>
          </header>
          <div className="operations-list">
            {visibleProjects.length ? visibleProjects.map((project) => (
              <button type="button" key={project.id} onClick={() => { setFormError(''); setProjectEditor(project) }}>
                <span className="operation-number">№ {project.number}</span>
                <strong title={project.name}>{project.name}</strong>
                <small>{project.clientNameSnapshot}</small>
                <ChevronRight size={16} />
              </button>
            )) : <p className="operations-empty">Добавьте первый проект и свяжите его с клиентом.</p>}
          </div>
          <button className="operations-all" type="button" onClick={() => { setQuery(''); setRegistry('projects') }}>
            Все проекты <ChevronRight size={15} />
          </button>
        </article>

        <article className="operations-card">
          <header>
            <div>
              <ReceiptText size={18} />
              <span>
                <strong>{invoices.length}</strong> счетов
                {overdue ? <b className="invoice-alert overdue">{overdue} просрочено</b> : null}
                {dueSoon ? <b className="invoice-alert soon">{dueSoon} скоро</b> : null}
              </span>
            </div>
            <button type="button" onClick={() => openInvoiceEditor(invoiceDraft())}>
              <Plus size={16} /> Счёт
            </button>
          </header>
          <div className="operations-list invoice-list">
            {visibleInvoices.length ? visibleInvoices.map((invoice) => {
              const timing = invoiceTiming(invoice)
              return (
                <button type="button" className={`invoice-${timing.status}`} key={invoice.id} onClick={() => openInvoiceEditor(invoice)}>
                  <span className="operation-number">Счёт № {invoice.number}</span>
                  <strong>{money(invoice.amountKopecks)}</strong>
                  <small>{invoice.clientNameSnapshot}</small>
                  <span className={`invoice-timing ${timing.tone}`}>{timing.label}</span>
                </button>
              )
            }) : <p className="operations-empty">Выставленные счета появятся здесь.</p>}
          </div>
          <button className="operations-all" type="button" onClick={() => { setQuery(''); setRegistry('invoices') }}>
            Все счета <ChevronRight size={15} />
          </button>
        </article>
      </div>

      {registry ? (
        <div
          className={`operations-overlay ${mode !== 'overview' ? 'operations-page-registry' : ''}`}
          role={mode === 'overview' ? 'dialog' : undefined}
          aria-modal={mode === 'overview' ? 'true' : undefined}
          aria-label={registry === 'projects' ? 'Все проекты' : 'Все счета'}
        >
          <div className="operations-registry">
            <header>
              <div>
                <span className="eyebrow">{registry === 'projects' ? 'Реестр проектов' : 'Контроль счетов'}</span>
                <h2>{registry === 'projects' ? `${projects.length} проектов` : `${invoices.length} счетов`}</h2>
              </div>
              {mode === 'overview' ? (
                <button className="icon-button" type="button" onClick={() => setRegistry(null)} aria-label="Закрыть"><X size={20} /></button>
              ) : (
                <button
                  className="button primary"
                  type="button"
                  onClick={() => registry === 'projects'
                    ? setProjectEditor(projectDraft(clients))
                    : openInvoiceEditor(invoiceDraft())}
                >
                  <Plus size={16} /> {registry === 'projects' ? 'Новый проект' : 'Новый счёт'}
                </button>
              )}
            </header>
            <input type="search" placeholder="Номер, название или клиент…" value={query} onChange={(event) => setQuery(event.target.value)} />
            <div className="registry-list">
              {registry === 'projects' ? registryProjects.map((project) => (
                <button type="button" key={project.id} onClick={() => { setFormError(''); setProjectEditor(project) }}>
                  <span>№ {project.number}</span><strong>{project.name}</strong><small>{project.clientNameSnapshot}</small>
                  <b>{project.status === 'active' ? 'Активный' : 'Архив'}</b>
                </button>
              )) : registryInvoices.map((invoice) => {
                const timing = invoiceTiming(invoice)
                return (
                  <button type="button" key={invoice.id} onClick={() => openInvoiceEditor(invoice)}>
                    <span>Счёт № {invoice.number}</span><strong>{money(invoice.amountKopecks)}</strong>
                    <small>{invoice.clientNameSnapshot}</small><b className={timing.tone}>{timing.label}</b>
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      ) : null}

      {projectEditor ? (
        <div className="operations-overlay" role="dialog" aria-modal="true" aria-label="Редактирование проекта">
          <form className="operations-editor" onSubmit={(event) => { event.preventDefault(); submitProject() }}>
            <header><h2>{projects.some((item) => item.id === projectEditor.id) ? 'Изменить проект' : 'Новый проект'}</h2><button className="icon-button" type="button" onClick={() => setProjectEditor(null)} aria-label="Закрыть редактор проекта"><X size={20} /></button></header>
            <label><span>Номер проекта</span><input required value={projectEditor.number} onChange={(event) => setProjectEditor({ ...projectEditor, number: event.target.value })} /></label>
            <label><span>Название</span><textarea required rows={4} value={projectEditor.name} onChange={(event) => setProjectEditor({ ...projectEditor, name: event.target.value })} /></label>
            <label><span>Клиент</span><select required value={projectEditor.clientId} onChange={(event) => chooseProjectClient(event.target.value)}><option value="">Выберите клиента</option>{clients.map((client) => <option value={client.id} key={client.id}>{client.company}</option>)}</select></label>
            <label><span>Состояние</span><select value={projectEditor.status} onChange={(event) => setProjectEditor({ ...projectEditor, status: event.target.value as WorkProject['status'] })}><option value="active">Активный</option><option value="archived">Архив</option></select></label>
            <footer>
              {projects.some((item) => item.id === projectEditor.id) ? <button className="danger-link" type="button" onClick={() => { if (onDeleteProject(projectEditor)) setProjectEditor(null) }}>Удалить</button> : <span />}
              <button className="button primary" type="submit">Сохранить</button>
            </footer>
          </form>
        </div>
      ) : null}

      {invoiceEditor ? (
        <div className="operations-overlay" role="dialog" aria-modal="true" aria-label="Редактирование счёта">
          <form className="operations-editor invoice-editor" onSubmit={(event) => { event.preventDefault(); submitInvoice() }}>
            <header><h2>{invoices.some((item) => item.id === invoiceEditor.id) ? 'Изменить счёт' : 'Новый счёт'}</h2><button className="icon-button" type="button" onClick={() => setInvoiceEditor(null)} aria-label="Закрыть редактор счёта"><X size={20} /></button></header>
            <div className="form-grid">
              <label><span>Номер счёта</span><input required value={invoiceEditor.number} onChange={(event) => setInvoiceEditor({ ...invoiceEditor, number: event.target.value })} /></label>
              <label><span>Сумма, ₽</span><input required min="0" step="0.01" type="number" value={invoiceEditor.amountKopecks / 100 || ''} onChange={(event) => setInvoiceEditor({ ...invoiceEditor, amountKopecks: Math.round(Number(event.target.value) * 100) })} /></label>
              <label className="span-2 invoice-client-field">
                <span>Клиент</span>
                <div className="invoice-client-picker">
                  <Search size={17} aria-hidden="true" />
                  <input
                    type="search"
                    role="combobox"
                    aria-expanded={clientPickerOpen}
                    aria-controls="invoice-client-options"
                    aria-autocomplete="list"
                    placeholder="Начните вводить название или ИНН…"
                    required={!invoiceEditor.clientId}
                    value={clientQuery}
                    onFocus={() => setClientPickerOpen(true)}
                    onBlur={() => window.setTimeout(() => setClientPickerOpen(false), 120)}
                    onChange={(event) => {
                      setClientQuery(event.target.value)
                      setClientPickerOpen(true)
                      if (invoiceEditor.clientId) {
                        setInvoiceEditor({
                          ...invoiceEditor,
                          clientId: '',
                          clientNameSnapshot: '',
                          projectId: null,
                          projectNameSnapshot: '',
                        })
                      }
                    }}
                  />
                  {invoiceEditor.clientId ? <b>Выбран</b> : null}
                  {clientPickerOpen ? (
                    <div className="invoice-client-options" id="invoice-client-options" role="listbox">
                      {matchingClients.map((client) => (
                        <button
                          type="button"
                          role="option"
                          aria-selected={client.id === invoiceEditor.clientId}
                          key={client.id}
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => chooseInvoiceClient(client.id)}
                        >
                          <strong>{displayCompanyName(client.company)}</strong>
                          <small>
                            {client.company}
                            {client.taxId ? ` · ИНН ${client.taxId}` : ''}
                          </small>
                        </button>
                      ))}
                      {!matchingClients.length ? (
                        <p>Совпадений не найдено.</p>
                      ) : null}
                      <button
                        className="create-client-option"
                        type="button"
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => setNewClientEditor({
                          company: clientQuery.trim(),
                          taxId: '',
                          paymentTermsDays: 30,
                        })}
                      >
                        <UserPlus size={16} />
                        {clientQuery.trim() ? `Создать клиента «${clientQuery.trim()}»` : 'Создать нового клиента'}
                      </button>
                    </div>
                  ) : null}
                </div>
                {invoiceEditor.clientId ? (
                  <small className="selected-client-caption">
                    Полное название: {invoiceEditor.clientNameSnapshot}
                  </small>
                ) : null}
              </label>
              <label className="span-2"><span>Проект</span><select value={invoiceEditor.projectId ?? ''} onChange={(event) => { const project = projects.find((item) => item.id === event.target.value); setInvoiceEditor({ ...invoiceEditor, projectId: project?.id ?? null, projectNameSnapshot: project?.name ?? '' }) }}><option value="">Без проекта</option>{projects.filter((project) => project.clientId === invoiceEditor.clientId).map((project) => <option value={project.id} key={project.id}>№ {project.number} · {project.name}</option>)}</select></label>
              <label><span>Дата счёта</span><input type="date" required value={invoiceEditor.issuedOn} onChange={(event) => setInvoiceEditor({ ...invoiceEditor, issuedOn: event.target.value })} /></label>
              <label><span>Статус</span><select value={invoiceEditor.status} onChange={(event) => changeInvoiceStatus(event.target.value as InvoiceStatus)}><option value="issued">Счёт выставлен</option><option value="shipped">Отгружено</option><option value="paid">Оплачено</option><option value="cancelled">Отменено</option></select></label>
              {invoiceEditor.status === 'shipped' || invoiceEditor.status === 'paid' ? <label><span>Дата полной отгрузки</span><input type="date" required min={invoiceEditor.issuedOn} value={invoiceEditor.shippedOn ?? ''} onChange={(event) => setInvoiceEditor({ ...invoiceEditor, shippedOn: event.target.value })} /></label> : null}
              {invoiceEditor.status === 'paid' ? <label><span>Дата оплаты</span><input type="date" required min={invoiceEditor.shippedOn ?? invoiceEditor.issuedOn} value={invoiceEditor.paidOn ?? ''} onChange={(event) => setInvoiceEditor({ ...invoiceEditor, paidOn: event.target.value })} /></label> : null}
              <label>
                <span>Условия оплаты</span>
                <select
                  value={STANDARD_PAYMENT_TERMS.includes(invoiceEditor.paymentTermsDays)
                    ? String(invoiceEditor.paymentTermsDays)
                    : 'custom'}
                  onChange={(event) => setInvoiceEditor({
                    ...invoiceEditor,
                    paymentTermsDays: event.target.value === 'custom' ? 1 : Number(event.target.value),
                  })}
                >
                  <option value="0">Предоплата</option>
                  <option value="30">Отсрочка 30 дней</option>
                  <option value="45">Отсрочка 45 дней</option>
                  <option value="60">Отсрочка 60 дней</option>
                  <option value="90">Отсрочка 90 дней</option>
                  <option value="custom">Своё значение</option>
                </select>
              </label>
              {!STANDARD_PAYMENT_TERMS.includes(invoiceEditor.paymentTermsDays) ? (
                <label>
                  <span>Своё значение, дней</span>
                  <input type="number" min="1" max="365" required value={invoiceEditor.paymentTermsDays} onChange={(event) => setInvoiceEditor({ ...invoiceEditor, paymentTermsDays: Number(event.target.value) })} />
                </label>
              ) : null}
            </div>
            {formError ? <p className="operations-form-error">{formError}</p> : null}
            {(invoiceEditor.status === 'shipped' || invoiceEditor.status === 'paid') ? (
              <div className={`invoice-preview ${invoiceTiming(invoiceEditor).tone}`}>
                <AlertTriangle size={17} /> {invoiceTiming(invoiceEditor).label}. Календарные дни от полной отгрузки.
              </div>
            ) : null}
            <footer>
              {invoices.some((item) => item.id === invoiceEditor.id) ? <button className="danger-link" type="button" onClick={() => { if (onDeleteInvoice(invoiceEditor)) setInvoiceEditor(null) }}>Удалить</button> : <span />}
              <button className="button primary" type="submit">Сохранить</button>
            </footer>
          </form>
        </div>
      ) : null}

      {newClientEditor ? (
        <div className="operations-overlay new-client-overlay" role="dialog" aria-modal="true" aria-label="Создание нового клиента">
          <form className="operations-editor new-client-editor" onSubmit={(event) => { event.preventDefault(); submitNewClient() }}>
            <header>
              <div>
                <span className="eyebrow">Новый клиент</span>
                <h2>Добавить в клиентскую базу</h2>
              </div>
              <button className="icon-button" type="button" onClick={() => setNewClientEditor(null)} aria-label="Закрыть создание клиента"><X size={20} /></button>
            </header>
            <p>Клиент сохранится в общей базе и сразу подставится в создаваемый счёт.</p>
            <label>
              <span>Название компании</span>
              <input autoFocus required value={newClientEditor.company} onChange={(event) => setNewClientEditor({ ...newClientEditor, company: event.target.value })} />
              {newClientEditor.company.trim() ? <small>В списке: {displayCompanyName(newClientEditor.company)}</small> : null}
            </label>
            <label>
              <span>ИНН</span>
              <input inputMode="numeric" maxLength={12} value={newClientEditor.taxId} onChange={(event) => setNewClientEditor({ ...newClientEditor, taxId: event.target.value.replace(/\D/g, '') })} />
            </label>
            <label>
              <span>Условия оплаты</span>
              <select
                value={STANDARD_PAYMENT_TERMS.includes(newClientEditor.paymentTermsDays)
                  ? String(newClientEditor.paymentTermsDays)
                  : 'custom'}
                onChange={(event) => setNewClientEditor({
                  ...newClientEditor,
                  paymentTermsDays: event.target.value === 'custom' ? 1 : Number(event.target.value),
                })}
              >
                <option value="0">Предоплата</option>
                <option value="30">Отсрочка 30 дней</option>
                <option value="45">Отсрочка 45 дней</option>
                <option value="60">Отсрочка 60 дней</option>
                <option value="90">Отсрочка 90 дней</option>
                <option value="custom">Своё значение</option>
              </select>
            </label>
            {!STANDARD_PAYMENT_TERMS.includes(newClientEditor.paymentTermsDays) ? (
              <label>
                <span>Своё значение, дней</span>
                <input type="number" min="1" max="365" required value={newClientEditor.paymentTermsDays} onChange={(event) => setNewClientEditor({ ...newClientEditor, paymentTermsDays: Number(event.target.value) })} />
              </label>
            ) : null}
            <footer>
              <button className="button secondary" type="button" onClick={() => setNewClientEditor(null)}>Отмена</button>
              <button className="button primary" type="submit"><UserPlus size={17} /> Создать и выбрать</button>
            </footer>
          </form>
        </div>
      ) : null}
    </section>
  )
}
